<!-- 某个记录的跟进记录列表，目前主要用于 CRM 客户、商机等详情界面 -->
<template>
  <!-- 操作栏 -->
  <el-row class="mb-10px" justify="end">
    <el-button @click="openForm">
      <Icon class="mr-5px" icon="ep:edit" />
      写跟进
    </el-button>
  </el-row>
  <!-- 列表 -->
  <ContentWrap>
    <!-- ★ 列定义与「客户跟进 → 全部跟进记录」列表页（components/FollowUpList.vue）保持完全一致，
         以原型图 `原型图/客户/客户跟进/全部跟进记录-列表页.png` 为准。
         ⚠️ 两个壳的列必须同步改，只改一边会出现「同一个跟进记录、不同页面列不一样」。
         ⚠️ 顺序：「跟进状态」在「跟进方式」之前，不要对调。 -->
    <el-table v-loading="loading" :data="list" :show-overflow-tooltip="true" :stripe="true">
      <el-table-column align="center" fixed="left" label="客户" prop="customerName" width="160" />
      <el-table-column align="center" label="联系人" prop="contacts" width="140">
        <template #default="scope">
          <span v-for="(contact, index) in scope.row.contacts" :key="contact.id">
            <el-link
              :underline="false"
              class="ml-5px"
              type="primary"
              @click="openContactDetail(contact.id)"
            >
              {{ contact.name }}
            </el-link>
            <span v-if="index < scope.row.contacts.length - 1">、</span>
          </span>
        </template>
      </el-table-column>
      <el-table-column align="center" label="跟进内容" prop="content" width="240" />
      <el-table-column align="center" label="跟进状态" prop="followStatus" width="110">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.CRM_FOLLOW_UP_STATUS" :value="scope.row.followStatus" />
        </template>
      </el-table-column>
      <el-table-column align="center" label="跟进方式" prop="type" width="110">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.CRM_FOLLOW_UP_TYPE" :value="scope.row.type" />
        </template>
      </el-table-column>
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="上次跟进时间"
        prop="followTime"
        width="180"
      />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="下次跟进时间"
        prop="nextTime"
        width="180"
      />
      <el-table-column align="center" label="跟进备注" prop="remark" width="180" />
      <el-table-column align="center" label="申请人" prop="creatorName" width="110" />
      <el-table-column align="center" label="申请人部门" prop="creatorDeptName" width="130" />
      <el-table-column align="center" label="单号" prop="no" width="170" />
      <el-table-column
        align="center"
        fixed="right"
        :show-overflow-tooltip="false"
        label="操作"
        width="120"
      >
        <template #default="scope">
          <div class="crm-followup-action">
            <el-button
              v-hasPermi="['crm:follow-up-record:update']"
              link
              type="primary"
              @click="openForm('update', scope.row.id)"
            >
              编辑
            </el-button>
            <el-button
              v-hasPermi="['crm:follow-up-record:delete']"
              link
              type="danger"
              @click="handleDelete(scope.row.id)"
            >
              删除
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <!-- 分页 -->
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 表单弹窗：新增 / 修改跟进记录
       ★ 表单版本：由 `useNewForm` 决定
          true  → 新版「新增跟进记录」FollowUpEditForm（与「客户跟进 → 全部跟进」同一个表单）
          false → 旧版「添加跟进记录」FollowUpRecordForm（**文件保留**，供随时切回）
         切换方式：改下面 `USE_NEW_FOLLOW_UP_FORM` 常量即可全局切回旧表单 -->
  <FollowUpEditForm v-if="useNewForm" ref="formRef" lock-customer @success="getList" />
  <FollowUpRecordForm v-else ref="formRef" @success="getList" />
</template>

<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import { DICT_TYPE } from '@/utils/dict'
import { FollowUpRecordApi, FollowUpRecordVO } from '@/api/crm/followup'
import FollowUpRecordForm from './FollowUpRecordForm.vue'
// 新版「新增跟进记录」表单（与「客户跟进 → 全部跟进」共用）
import FollowUpEditForm from './components/FollowUpEditForm.vue'

/** 跟进记录列表 */
defineOptions({ name: 'FollowUpRecord' })

/** ★ 全局开关：改回 false 即整体退回旧版「添加跟进记录」表单 */
const USE_NEW_FOLLOW_UP_FORM = true

const props = defineProps<{
  bizType: number
  bizId: number
  /**
   * 所属客户编号（可选）。
   * 新版「新增跟进记录」表单**必填客户**，故由详情页把所在记录的客户传进来预置。
   * - 客户 / 商机 / 联系人 / 合同详情：都能取到 `xxx.customerId`
   * - **线索详情不传**（线索是尚未转化的潜在客户，没有客户）⇒ 自动落回旧表单
   */
  customerId?: number
  /**
   * 所属客户名称（可选，**必须与 `customerId` 成对传**）。
   * ★ 表单客户下拉的数据源 `/crm/customer/simple-list` 只返回「当前登录人有读权限」的客户，
   *   且后端会 `isNotNull(owner_user_id)` 排除公海客户 —— 详情页正在看的这个客户
   *   完全可能不在该集合里。此时 `el-select` 找不到 option 的 label，就会把 id 当文案渲染
   *   （现象：客户框显示裸 `56`）。把名称一起传进来，表单即可零请求补出正确选项。
   */
  customerName?: string
}>()

/**
 * 是否使用新版表单。
 * ★ 必须有客户才可用：新版表单硬编码 `bizType = CRM_CUSTOMER` 且客户必填，
 * 线索这类「没有客户」的业务传进来会得到一个空的锁定客户框 ⇒ 自动退回旧表单。
 */
const useNewForm = computed(() => USE_NEW_FOLLOW_UP_FORM && !!props.customerId)
const message = useMessage() // 消息弹窗
const { t } = useI18n() // 国际化

const loading = ref(true) // 列表的加载中
const list = ref<FollowUpRecordVO[]>([]) // 列表的数据
const total = ref(0) // 列表的总页数
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  bizType: 0,
  bizId: 0
})

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    const data = await FollowUpRecordApi.getFollowUpRecordPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 添加/修改操作 */
const formRef = ref<any>()
const openForm = (type: string, id?: number) => {
  if (useNewForm.value) {
    // 新版：客户已由详情上下文给出并锁定，带上「编号 + 名称」两项
    // ★ 名称不可省：不传则客户下拉可能解析不出 label，把客户框显示成裸 id
    // ★ 编辑时只传 id：客户由表单自己按 `data.customerId` 回填，不要覆盖成当前页上下文
    if (type === 'create') {
      formRef.value?.open('create', undefined, props.customerId, props.customerName)
    } else {
      formRef.value?.open('update', id)
    }
  } else {
    formRef.value?.open(props.bizType, props.bizId)
  }
}

/** 删除按钮操作 */
const handleDelete = async (id: number) => {
  try {
    // 删除的二次确认
    await message.delConfirm()
    // 发起删除
    await FollowUpRecordApi.deleteFollowUpRecord(id)
    message.success(t('common.delSuccess'))
    // 刷新列表
    await getList()
  } catch {}
}

/** 打开联系人详情 */
const { push } = useRouter()
const openContactDetail = (id: number) => {
  push({ name: 'CrmContactDetail', params: { id } })
}

watch(
  () => props.bizId,
  () => {
    queryParams.bizType = props.bizType
    queryParams.bizId = props.bizId
    getList()
  }
)
</script>

<style lang="scss" scoped>
/**
 * 操作列对齐：统一交给 flex 垂直居中，并把 el-button 默认的 margin-left 换成 gap，
 * 避免按钮之间的间距不一致。
 * ⚠️ 与 `components/FollowUpList.vue` 同名同款，两处要同步改。
 */
.crm-followup-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  :deep(.el-button + .el-button) {
    margin-left: 0;
  }
}
</style>
